/**
 * Player Controls - UI components for player control
 * Uses spatial navigation for TV remote support
 */
import React, { memo, useEffect } from "react";
import {
  useFocusable,
  FocusContext,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import { usePlayer, usePlayerState } from "../core/PlayerContext";
import { useNavigate } from "react-router-dom";

// Icons as inline SVGs for legacy compatibility
var PlayIcon = function () {
  return React.createElement(
    "svg",
    { viewBox: "0 0 24 24", fill: "currentColor", width: 64, height: 64 },
    React.createElement("path", { d: "M8 5v14l11-7z" }),
  );
};

var PauseIcon = function () {
  return React.createElement(
    "svg",
    { viewBox: "0 0 24 24", fill: "currentColor", width: 64, height: 64 },
    React.createElement("path", { d: "M6 19h4V5H6v14zm8-14v14h4V5h-4z" }),
  );
};

var SeekBackIcon = function () {
  return React.createElement(
    "svg",
    { viewBox: "0 0 24 24", fill: "currentColor", width: 56, height: 56 },
    React.createElement("path", {
      d: "M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z",
    }),
  );
};

var SeekForwardIcon = function () {
  return React.createElement(
    "svg",
    { viewBox: "0 0 24 24", fill: "currentColor", width: 56, height: 56 },
    React.createElement("path", {
      d: "M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z",
    }),
  );
};

var VolumeIcon = function () {
  return React.createElement(
    "svg",
    { viewBox: "0 0 24 24", fill: "currentColor", width: 56, height: 56 },
    React.createElement("path", {
      d: "M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z",
    }),
  );
};

var VolumeMuteIcon = function () {
  return React.createElement(
    "svg",
    { viewBox: "0 0 24 24", fill: "currentColor", width: 56, height: 56 },
    React.createElement("path", {
      d: "M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z",
    }),
  );
};

var BackIcon = function () {
  return React.createElement(
    "svg",
    { viewBox: "0 0 24 24", fill: "currentColor", width: 56, height: 56 },
    React.createElement("path", {
      d: "M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z",
    }),
  );
};

var FullscreenIcon = function () {
  return React.createElement(
    "svg",
    { viewBox: "0 0 24 24", fill: "currentColor", width: 56, height: 56 },
    React.createElement("path", {
      d: "M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z",
    }),
  );
};

var ExitFullscreenIcon = function () {
  return React.createElement(
    "svg",
    { viewBox: "0 0 24 24", fill: "currentColor", width: 56, height: 56 },
    React.createElement("path", {
      d: "M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z",
    }),
  );
};

/**
 * Format time to MM:SS or HH:MM:SS
 */
function formatTime(seconds) {
  if (isNaN(seconds) || !isFinite(seconds)) return "00:00";

  var hrs = Math.floor(seconds / 3600);
  var mins = Math.floor((seconds % 3600) / 60);
  var secs = Math.floor(seconds % 60);

  var pad = function (n) {
    return n < 10 ? "0" + n : String(n);
  };

  if (hrs > 0) {
    return pad(hrs) + ":" + pad(mins) + ":" + pad(secs);
  }
  return pad(mins) + ":" + pad(secs);
}

export function getPlayerVerticalTarget(controlKey, direction, isLive) {
  var bottomTarget = isLive ? "fullscreenButton" : "progressBar";
  var lowerUpTarget = isLive ? "playButton" : "progressBar";
  var targets = {
    backButton: { down: "playButton" },
    muteButton: { down: "playButton" },
    seekBackward: { up: "backButton", down: bottomTarget },
    playButton: { up: "backButton", down: bottomTarget },
    seekForward: { up: "backButton", down: bottomTarget },
    progressBar: { up: "playButton", down: "fullscreenButton" },
    episodesButton: { up: lowerUpTarget },
    settingsButton: { up: lowerUpTarget },
    captionsButton: { up: lowerUpTarget },
    fullscreenButton: { up: lowerUpTarget },
  };
  var controlTargets = targets[controlKey];
  return controlTargets ? controlTargets[direction] || null : null;
}

function handleVerticalNavigation(controlKey, direction, isLive) {
  var target = getPlayerVerticalTarget(controlKey, direction, isLive);
  if (!target) return true;
  // Key activity makes a hidden controls overlay visible. Defer focus until
  // that visibility update has committed so legacy Chromium sees the target.
  setTimeout(function () {
    setFocus(target);
  }, 0);
  return false;
}

/**
 * Play/Pause Button
 */
var PlayButton = memo(function PlayButton(props) {
  var focusKey = props.focusKey || "playButton";
  var isLive = !!props.isLive;
  var player = usePlayer();
  var paused = usePlayerState("paused");

  var { focused, ref, focusSelf } = useFocusable({
    focusKey: focusKey,
    onEnterPress: function () {
      player.togglePlay();
    },
    onArrowPress: function (direction) {
      return handleVerticalNavigation(focusKey, direction, isLive);
    },
  });

  useEffect(
    function () {
      if (focused) {
        player.showControls(); // Just show controls, don't manage timers
      }
    },
    [focused, player],
  );

  // Auto-focus on mount
  useEffect(
    function () {
      focusSelf();
    },
    [focusSelf],
  );

  return React.createElement(
    "button",
    {
      ref: ref,
      className: "mnb-btn mnb-play-btn " + (focused ? "focused" : ""),
      onClick: function () {
        player.togglePlay();
      },
      onMouseEnter: function () {
        if (focusSelf) focusSelf();
      },
      type: "button",
    },
    paused ? React.createElement(PlayIcon) : React.createElement(PauseIcon),
  );
});

/**
 * Seek Forward Button
 */
var SeekForwardButton = memo(function SeekForwardButton(props) {
  var seconds = props.seconds || 15;
  var focusKey = props.focusKey || "seekForward";
  var player = usePlayer();

  var { focused, ref, focusSelf } = useFocusable({
    focusKey: focusKey,
    onEnterPress: function () {
      player.seekBy(seconds);
    },
    onArrowPress: function (direction) {
      return handleVerticalNavigation(focusKey, direction, false);
    },
  });

  useEffect(
    function () {
      if (focused) {
        player.showControls();
      }
    },
    [focused, player],
  );

  return React.createElement(
    "button",
    {
      ref: ref,
      className: "mnb-btn mnb-seek-btn " + (focused ? "focused" : ""),
      onClick: function () {
        player.seekBy(seconds);
      },
      onMouseEnter: function () {
        if (focusSelf) focusSelf();
      },
      type: "button",
    },
    React.createElement(SeekForwardIcon),
    React.createElement("span", { className: "mnb-seek-label" }, "+" + seconds),
  );
});

/**
 * Seek Backward Button
 */
var SeekBackwardButton = memo(function SeekBackwardButton(props) {
  var seconds = props.seconds || 15;
  var focusKey = props.focusKey || "seekBackward";
  var player = usePlayer();

  var { focused, ref, focusSelf } = useFocusable({
    focusKey: focusKey,
    onEnterPress: function () {
      player.seekBy(-seconds);
    },
    onArrowPress: function (direction) {
      return handleVerticalNavigation(focusKey, direction, false);
    },
  });

  useEffect(
    function () {
      if (focused) {
        player.showControls();
      }
    },
    [focused, player],
  );

  return React.createElement(
    "button",
    {
      ref: ref,
      className: "mnb-btn mnb-seek-btn " + (focused ? "focused" : ""),
      onClick: function () {
        player.seekBy(-seconds);
      },
      onMouseEnter: function () {
        if (focusSelf) focusSelf();
      },
      type: "button",
    },
    React.createElement(SeekBackIcon),
    React.createElement("span", { className: "mnb-seek-label" }, "-" + seconds),
  );
});

/**
 * Mute Button
 */
var MuteButton = memo(function MuteButton(props) {
  var focusKey = props.focusKey || "muteButton";
  var isLive = !!props.isLive;
  var player = usePlayer();
  var muted = usePlayerState("muted");

  var { focused, ref, focusSelf } = useFocusable({
    focusKey: focusKey,
    onEnterPress: function () {
      player.toggleMute();
    },
    onArrowPress: function (direction) {
      return handleVerticalNavigation(focusKey, direction, isLive);
    },
  });

  useEffect(
    function () {
      if (focused) {
        player.showControls();
      }
    },
    [focused, player],
  );

  return React.createElement(
    "button",
    {
      ref: ref,
      className: "mnb-btn mnb-mute-btn " + (focused ? "focused" : ""),
      onClick: function () {
        player.toggleMute();
      },
      onMouseEnter: function () {
        if (focusSelf) focusSelf();
      },
      type: "button",
    },
    muted
      ? React.createElement(VolumeMuteIcon)
      : React.createElement(VolumeIcon),
  );
});

/**
 * Fullscreen Button
 */
var FullscreenButton = memo(function FullscreenButton(props) {
  var focusKey = props.focusKey || "fullscreenButton";
  var isLive = !!props.isLive;
  var player = usePlayer();
  var fullscreen = usePlayerState("fullscreen");

  var { focused, ref, focusSelf } = useFocusable({
    focusKey: focusKey,
    onEnterPress: function () {
      player.toggleFullscreen();
    },
    onArrowPress: function (direction) {
      return handleVerticalNavigation(focusKey, direction, isLive);
    },
  });

  useEffect(
    function () {
      if (focused) {
        player.showControls();
      }
    },
    [focused, player],
  );

  return React.createElement(
    "button",
    {
      ref: ref,
      className: "mnb-btn mnb-fullscreen-btn " + (focused ? "focused" : ""),
      onClick: function () {
        player.toggleFullscreen();
      },
      onMouseEnter: function () {
        if (focusSelf) focusSelf();
      },
      type: "button",
    },
    fullscreen
      ? React.createElement(ExitFullscreenIcon)
      : React.createElement(FullscreenIcon),
  );
});

/**
 * Back Button
 */
var BackButton = memo(function BackButton(props) {
  var focusKey = props.focusKey || "backButton";
  var isLive = !!props.isLive;
  var navigate = useNavigate();

  var { focused, ref, focusSelf } = useFocusable({
    focusKey: focusKey,
    onEnterPress: function () {
      navigate(-1);
    },
    onArrowPress: function (direction) {
      return handleVerticalNavigation(focusKey, direction, isLive);
    },
  });

  return React.createElement(
    "button",
    {
      ref: ref,
      className: "mnb-btn mnb-back-btn " + (focused ? "focused" : ""),
      onClick: function () {
        navigate(-1);
      },
      onMouseEnter: function () {
        if (focusSelf) focusSelf();
      },
      type: "button",
    },
    React.createElement(BackIcon),
  );
});

/**
 * Settings Button
 */
var SettingsButton = memo(function SettingsButton(props) {
  var focusKey = props.focusKey || "settingsButton";
  var onPress = props.onPress;
  var label = props.label || "صدا و تصویر";
  var isLive = !!props.isLive;
  var player = usePlayer();

  var { focused, ref, focusSelf } = useFocusable({
    focusKey: focusKey,
    onEnterPress: function () {
      if (onPress) onPress();
    },
    onArrowPress: function (direction) {
      return handleVerticalNavigation(focusKey, direction, isLive);
    },
  });

  useEffect(
    function () {
      if (focused) {
        player.showControls();
      }
    },
    [focused, player],
  );

  return React.createElement(
    "button",
    {
      ref: ref,
      className: "mnb-text-btn " + (focused ? "focused" : ""),
      onClick: function () {
        if (onPress) onPress();
      },
      onMouseEnter: function () {
        if (focusSelf) focusSelf();
      },
      type: "button",
    },
    label,
  );
});

/**
 * Time Display
 */
var TimeDisplay = function TimeDisplay(props) {
  var isLive = props.isLive;
  var currentTime = usePlayerState("currentTime");
  var duration = usePlayerState("duration");

  if (isLive) {
    return React.createElement(
      "div",
      { className: "mnb-time-display live" },
      React.createElement("span", {
        style: {
          width: "15px",
          height: "15px",
          backgroundColor: "#EF4444",
          borderRadius: "50%",
          display: "inline-block",
          marginLeft: "8px",
          boxShadow: "0 0 6px #EF4444",
          animation: "pulseLive 1.5s infinite",
        },
      }),
      React.createElement(
        "span",
        { style: { color: "#EF4444", fontWeight: "bold", marginLeft: 10 } },
        "پخش زنده",
      ),
    );
  }

  return React.createElement(
    "div",
    { className: "mnb-time-display" },
    React.createElement(
      "span",
      { className: "mnb-time-current" },
      formatTime(currentTime),
    ),
    React.createElement("span", { className: "mnb-time-sep" }, " / "),
    React.createElement(
      "span",
      { className: "mnb-time-duration" },
      formatTime(duration),
    ),
  );
};

/**
 * Progress Bar - Focusable with seeking state
 * Left/Right keys seek, Up/Down navigate
 * Pauses during seek, resumes on release
 */
/**
 * Progress Bar - Focusable with seeking state
 * Left/Right keys seek with exponential acceleration on hold
 * Up/Down navigate out
 * Pauses during seek, resumes on release
 */
var ProgressBar = memo(function ProgressBar(props) {
  var isLive = props.isLive;
  var player = usePlayer();
  var currentTime = usePlayerState("currentTime");
  var duration = usePlayerState("duration");
  var buffered = usePlayerState("buffered");
  var paused = usePlayerState("paused");

  // Local seeking state
  var [isSeeking, setIsSeeking] = React.useState(false);
  var [seekTime, setSeekTime] = React.useState(0);
  var wasPlayingRef = React.useRef(false);

  // Seek acceleration state
  var lastPressTimeRef = React.useRef(0);
  var accelerationRef = React.useRef(1);
  var baseSeekStep = 5; // seconds base step

  // Use seekTime when seeking, otherwise currentTime
  var displayTime = isSeeking ? seekTime : currentTime;
  var progress = duration > 0 ? (displayTime / duration) * 100 : 0;
  var bufferedPercent = duration > 0 ? (buffered / duration) * 100 : 0;

  var { focused, ref, focusSelf } = useFocusable({
    focusKey: "progressBar",
    focusable: !isLive,
    onArrowPress: function (direction) {
      var verticalResult = handleVerticalNavigation(
        "progressBar",
        direction,
        false,
      );
      if (verticalResult === false) return false;

      if (direction === "left" || direction === "right") {
        var now = Date.now();
        var timeDiff = now - lastPressTimeRef.current;

        // If key is held or pressed rapidly ( < 250ms), increase acceleration
        if (timeDiff < 250) {
          // Capped exponential growth: 1x -> 1.5x -> 2.25x ... -> 16x (max)
          accelerationRef.current = Math.min(16, accelerationRef.current * 1.5);
        } else {
          accelerationRef.current = 1;
        }
        lastPressTimeRef.current = now;

        var currentStep = baseSeekStep * Math.floor(accelerationRef.current);

        // Start seeking
        if (!isSeeking) {
          wasPlayingRef.current = !paused;
          if (!paused) {
            player.pause();
          }
          setIsSeeking(true);
          setSeekTime(currentTime);
        }

        // Calculate new time
        var delta = direction === "right" ? currentStep : -currentStep;
        var newTime = Math.max(
          0,
          Math.min(duration, (isSeeking ? seekTime : currentTime) + delta),
        );
        setSeekTime(newTime);

        // Keep controls visible during seek
        player.showControls();

        // Don't propagate - we handle left/right ourselves
        return false;
      }
      // Allow up/down to propagate for normal navigation
      return true;
    },
    onBlur: function () {
      // Commit seek on blur
      if (isSeeking) {
        player.seek(seekTime);
        if (wasPlayingRef.current) {
          player.play();
        }
        setIsSeeking(false);
        accelerationRef.current = 1;
      }
    },
  });

  // Handle Enter to commit seek
  React.useEffect(
    function () {
      if (!focused) return;

      var handleKeyDown = function (e) {
        if ((e.key === "Enter" || e.keyCode === 13) && isSeeking) {
          player.seek(seekTime);
          if (wasPlayingRef.current) {
            player.play();
          }
          setIsSeeking(false);
          accelerationRef.current = 1;
          e.preventDefault();
          e.stopPropagation();
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return function () {
        window.removeEventListener("keydown", handleKeyDown);
      };
    },
    [focused, isSeeking, seekTime, player],
  );

  // Show controls while focused
  React.useEffect(
    function () {
      if (focused) {
        player.showControls();
      }
    },
    [focused, player],
  );

  var handleClick = function (e) {
    if (isLive) return;
    var rect = e.currentTarget.getBoundingClientRect();
    var x = e.clientX - rect.left;
    var percent = x / rect.width;
    player.seek(percent * duration);
  };

  if (isLive) {
    return React.createElement(
      "div",
      { className: "mnb-progress-container live" },
      React.createElement(
        "div",
        {
          className: "mnb-progress-bar live",
          style: { pointerEvents: "none" },
        },
        React.createElement("div", {
          className: "mnb-progress-track",
          style: { background: "rgba(239, 68, 68, 0.3)" },
        }),
        React.createElement("div", {
          className: "mnb-progress-fill",
          style: {
            width: "100%",
            background: "#EF4444",
            boxShadow: "0 0 8px #EF4444",
          },
        }),
      ),
    );
  }

  return React.createElement(
    "div",
    { className: "mnb-progress-container" },
    React.createElement(
      "div",
      {
        ref: ref,
        className:
          "mnb-progress-bar " +
          (focused ? "focused" : "") +
          (isSeeking ? " seeking" : ""),
        onClick: handleClick,
        onMouseEnter: function () {
          if (focusSelf) focusSelf();
        },
        tabIndex: 0,
      },
      React.createElement("div", { className: "mnb-progress-track" }),
      React.createElement("div", {
        className: "mnb-progress-buffered",
        style: { width: bufferedPercent + "%" },
      }),
      React.createElement("div", {
        className: "mnb-progress-fill",
        style: { width: progress + "%" },
      }),
      React.createElement("div", {
        className: "mnb-progress-thumb " + (focused ? "focused" : ""),
        style: { left: progress + "%" },
      }),
    ),
  );
});

/**
 * Top Controls Bar
 */
var TopControls = function TopControls(props) {
  var title = props.title;
  var subtitle = props.subtitle;
  var isLive = !!props.isLive;

  var { ref, focusKey } = useFocusable({
    focusKey: "topControls",
    focusable: false,
    trackChildren: true,
    preferredChildFocusKey: "backButton",
  });

  return React.createElement(
    FocusContext.Provider,
    { value: focusKey },
    React.createElement(
      "div",
      { className: "mnb-controls-top", ref: ref },
      React.createElement(MuteButton, { isLive: isLive }),
      React.createElement(
        "div",
        { className: "mnb-title-container" },
        React.createElement("h3", { className: "mnb-title" }, title),
        subtitle
          ? React.createElement(
              "span",
              { className: "mnb-subtitle" },
              " | " + subtitle,
            )
          : null,
      ),
      React.createElement(BackButton, { isLive: isLive }),
    ),
  );
};

/**
 * Middle Controls (Play, Seek)
 */
var MiddleControls = function MiddleControls(props) {
  var isLive = props.isLive;
  var { ref, focusKey } = useFocusable({
    focusKey: "middleControls",
    focusable: false,
    trackChildren: true,
    preferredChildFocusKey: "playButton",
  });

  return React.createElement(
    FocusContext.Provider,
    { value: focusKey },
    React.createElement(
      "div",
      { className: "mnb-controls-middle", ref: ref },
      !isLive ? React.createElement(SeekBackwardButton) : null,
      !isLive ? React.createElement("div", { className: "mnb-spacer" }) : null,
      React.createElement(PlayButton, { isLive: isLive }),
      !isLive ? React.createElement("div", { className: "mnb-spacer" }) : null,
      !isLive ? React.createElement(SeekForwardButton) : null,
    ),
  );
};

/**
 * Bottom Controls (Progress, Time, Settings)
 */
var BottomControls = function BottomControls(props) {
  var onSettingsPress = props.onSettingsPress;
  var onCaptionsPress = props.onCaptionsPress;
  var onEpisodesPress = props.onEpisodesPress;
  var showEpisodesButton = props.showEpisodesButton;
  var hideSettings = props.hideSettings;
  var hideCaptions = props.hideCaptions;
  var isLive = props.isLive;

  var { ref, focusKey } = useFocusable({
    focusKey: "bottomControls",
    focusable: false,
    trackChildren: true,
    preferredChildFocusKey: isLive ? "fullscreenButton" : "progressBar",
  });

  return React.createElement(
    FocusContext.Provider,
    { value: focusKey },
    React.createElement(
      "div",
      { className: "mnb-controls-bottom", ref: ref },
      React.createElement(ProgressBar, { isLive: isLive }),
      React.createElement(
        "div",
        { className: "mnb-controls-row" },
        React.createElement(TimeDisplay, { isLive: isLive }),
        React.createElement("div", { className: "mnb-flex-spacer" }),
        showEpisodesButton
          ? React.createElement(SettingsButton, {
              label: "قسمت‌ها",
              focusKey: "episodesButton",
              onPress: onEpisodesPress,
              isLive: isLive,
            })
          : null,
        showEpisodesButton
          ? React.createElement("div", { className: "mnb-spacer-sm" })
          : null,
        !hideSettings
          ? React.createElement(SettingsButton, {
              onPress: onSettingsPress,
              isLive: isLive,
            })
          : null,
        !hideSettings
          ? React.createElement("div", { className: "mnb-spacer-sm" })
          : null,
        !hideCaptions
          ? React.createElement(SettingsButton, {
              label: "تنظیمات زیرنویس",
              focusKey: "captionsButton",
              onPress: onCaptionsPress,
              isLive: isLive,
            })
          : null,
        !hideCaptions
          ? React.createElement("div", { className: "mnb-spacer-sm" })
          : null,
        React.createElement(FullscreenButton, { isLive: isLive }),
      ),
    ),
  );
};

export {
  PlayButton,
  SeekForwardButton,
  SeekBackwardButton,
  MuteButton,
  FullscreenButton,
  BackButton,
  SettingsButton,
  TimeDisplay,
  ProgressBar,
  TopControls,
  MiddleControls,
  BottomControls,
  formatTime,
};
